import React from 'react';
import {
  DocumentTextIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
  ScaleIcon,
  ClockIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

export default function DisciplinaryFormHelp() {
  return (
    <div className="bg-gradient-to-br from-red-50 to-orange-100 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <div className="bg-red-600 p-3 rounded-full">
              <ExclamationTriangleIcon className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Disciplinary Form Guide</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Learn how to properly document and manage disciplinary procedures with comprehensive investigation tracking and fair process documentation.
          </p>
        </div>

        {/* Overview Card */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <ShieldCheckIcon className="h-6 w-6 text-red-600 mr-3" />
            <h2 className="text-2xl font-semibold text-gray-900">Overview</h2>
          </div>
          <p className="text-gray-700 text-lg leading-relaxed">
            The Disciplinary Form provides a structured approach to documenting workplace incidents,
            conducting thorough investigations, and implementing appropriate disciplinary actions.
            This ensures fair treatment, legal compliance, and proper documentation of all disciplinary procedures.
          </p>
        </div>

        {/* Step-by-Step Process */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <ArrowRightIcon className="h-6 w-6 text-red-600 mr-3" />
            <h2 className="text-2xl font-semibold text-gray-900">Disciplinary Process Flow</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="bg-red-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-red-600 font-bold text-lg">1</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Incident Report</h3>
              <p className="text-gray-600 text-sm">Document the incident details, time, and location</p>
            </div>

            <div className="text-center">
              <div className="bg-red-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-red-600 font-bold text-lg">2</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Investigation</h3>
              <p className="text-gray-600 text-sm">Gather witness statements and employee perspective</p>
            </div>

            <div className="text-center">
              <div className="bg-red-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-red-600 font-bold text-lg">3</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Findings & Action</h3>
              <p className="text-gray-600 text-sm">Document investigation results and disciplinary actions</p>
            </div>

            <div className="text-center">
              <div className="bg-red-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-red-600 font-bold text-lg">4</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Support & Follow-up</h3>
              <p className="text-gray-600 text-sm">Provide support and set improvement deadlines</p>
            </div>
          </div>
        </div>

        {/* Key Features */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Incident Documentation */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <DocumentTextIcon className="h-6 w-6 text-red-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Incident Documentation</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Date & Time:</strong> Record when the incident occurred</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Location:</strong> Specify where the incident took place</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Description:</strong> Provide detailed account of what happened</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Policy Violation:</strong> Identify which policies or rules were breached</span>
              </li>
            </ul>
          </div>

          {/* Investigation Process */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <UserGroupIcon className="h-6 w-6 text-red-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Investigation Process</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Witness Selection:</strong> Choose multiple witnesses from staff list</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Witness Statements:</strong> Record statements from all witnesses</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Employee Perspective:</strong> Document employee's side of the story</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Agreement Check:</strong> Verify if employee agrees with the incident account</span>
              </li>
            </ul>
          </div>

          {/* Disciplinary Actions */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <ScaleIcon className="h-6 w-6 text-red-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Disciplinary Actions</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Investigation Steps:</strong> Document all steps taken during investigation</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Findings:</strong> Summarize investigation results and conclusions</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Action Proposed:</strong> Specify disciplinary measures (warning, suspension, etc.)</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Rationale:</strong> Explain the reasoning behind chosen disciplinary action</span>
              </li>
            </ul>
          </div>

          {/* Support & Improvement */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <ClockIcon className="h-6 w-6 text-red-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Support & Improvement</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Corrective Actions:</strong> Outline steps employee must take to improve</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Support Provided:</strong> Document company support and resources offered</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Improvement Deadline:</strong> Set realistic timelines for improvement</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Follow-up Tracking:</strong> Monitor progress and provide ongoing support</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Important Considerations */}
        <div className="bg-gradient-to-r from-red-600 to-orange-600 rounded-xl p-8 text-white mb-8">
          <div className="flex items-center mb-6">
            <ExclamationTriangleIcon className="h-8 w-8 mr-3" />
            <h2 className="text-2xl font-semibold">Important Considerations</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-xl font-semibold mb-3">Legal Compliance</h3>
              <ul className="space-y-2 text-red-100">
                <li>• Ensure fair and unbiased investigation process</li>
                <li>• Document all communications and meetings</li>
                <li>• Provide employee with opportunity to respond</li>
                <li>• Follow company disciplinary policy consistently</li>
                <li>• Consider employee's length of service and history</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-3">Best Practices</h3>
              <ul className="space-y-2 text-red-100">
                <li>• Act promptly but thoroughly investigate</li>
                <li>• Maintain confidentiality throughout process</li>
                <li>• Focus on facts rather than assumptions</li>
                <li>• Provide clear expectations and deadlines</li>
                <li>• Offer support and resources for improvement</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Tips & Guidelines */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Guidelines for Effective Disciplinary Procedures</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">📝 Documentation Tips</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Be factual and objective in all descriptions</li>
                <li>• Include specific examples and evidence</li>
                <li>• Use clear, professional language</li>
                <li>• Record dates, times, and locations accurately</li>
                <li>• Document all communications and agreements</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">⚖️ Fair Process</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Give employee opportunity to explain their perspective</li>
                <li>• Allow employee to bring a representative or colleague</li>
                <li>• Provide all relevant information before meetings</li>
                <li>• Ensure consistent application of disciplinary policies</li>
                <li>• Maintain records for appeals or reviews</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">🎯 Progressive Discipline</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Start with informal discussions for minor issues</li>
                <li>• Use verbal warnings before written warnings</li>
                <li>• Escalate only when necessary and appropriate</li>
                <li>• Consider employee's overall performance and history</li>
                <li>• Focus on improvement rather than punishment</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">📱 Mobile Usage</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Use landscape mode for better form readability</li>
                <li>• Save drafts when working on mobile devices</li>
                <li>• Review all sections before final submission</li>
                <li>• Ensure good internet connection for file uploads</li>
                <li>• Consider using a tablet for extended documentation</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}